/**
 * @file NursingEducationView.jsx
 * @author Fernando Ruano
 * @description Assesment In Psychology View Page
 */

// Dependencies
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';

// Commons
import styleGuideColors from 'commons/styleGuideColors';

// Components
import DashboardCard from 'components/DashboardCard';
import DashboardTitle from 'components/Charts/DashboardTitle';
import ProgressBar from 'components/Charts/ProgressBar';
import EmptyChart from 'components/EmptyChart';

// Helpers
import { transformToPercent } from '../helper';

/**
 * NursingEducation View
 * @param { Object } dashboardStaffActions - DashboardStaff Actions.
 * @return { Node } React node.
 */

function NursingEducationView({ dashboardStaffActions, clinicId }) {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const [chartData, setChartData] = useState(null);

  const { getNursingEducation } = dashboardStaffActions;

  const onGetData = (_, resp) => {
    const data = [];
    const colors = {
      'Caso 1 : NO': styleGuideColors.baxterBrandCyan,
      'Caso 2 : NO': styleGuideColors.baxterBrandBaxterBlue,
      'Caso 3 : NO': styleGuideColors.baxterBrandPurple
    };
    const titles = {
      'Caso 1 : NO': 'case1',
      'Caso 2 : NO': 'case2',
      'Caso 3 : NO': 'case3'
    };
    const subtitles = {
      'Caso 1 : NO': 'subCase1',
      'Caso 2 : NO': 'subCase2',
      'Caso 3 : NO': 'subCase3'
    };
    if (resp?.data) {
      let newData = transformToPercent(resp?.data, 'cantidad', 'value');
      newData = newData.filter((itm) => itm.clasificacion.includes('NO'));
      // Adapter information from service
      newData.forEach(({ clasificacion, value, cantidad }) => {
        const item = {
          title: translate(`dashboardStaff.${titles[clasificacion]}`),
          subtitle: translate(`dashboardStaff.${subtitles[clasificacion]}`),
          clasificacion,
          cantidad,
          percent: value,
          color: colors[clasificacion]
        };

        switch (clasificacion) {
          case 'Caso 1 : NO':
            data[0] = item;
            break;
          case 'Caso 2 : NO':
            data[1] = item;
            break;
          default:
            data[2] = item;
            break;
        }
      });
      setChartData(data);
    } else {
      setChartData([]);
    }
  };

  useEffect(() => {
    setChartData(null);
    getNursingEducation({ clinicId }, onGetData);
  }, [translate, clinicId]);

  const handleDetailClick = (index) => {
    // Get data by index and send by url param to detail list
    const { cantidad: totalItems, clasificacion: classification } =
      chartData[index];
    history.push(
      `/main/dashboard-detail/nursing-education/${classification}/${totalItems}`
    );
  };

  return (
    <DashboardCard id="F33" loading={!chartData}>
      <div className="flex flex-wrap">
        <DashboardTitle title={translate('dashboardStaff.nursingEducation')} />
        {chartData?.length === 0 && <EmptyChart />}
        <div className="px-2 w-full -mt-3">
          {chartData?.map(({ color, title, subtitle, percent }, index) => (
            <div onClick={() => handleDetailClick(index)} key={index}>
              <ProgressBar
                color={color}
                title={title}
                subtitle={subtitle}
                percent={percent}
                withLineBreak={true}
              />
            </div>
          ))}
        </div>
      </div>
    </DashboardCard>
  );
}

/**
 * Component definition of proptypes.
 */
NursingEducationView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getNursingEducation: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default NursingEducationView;
